/**
 * iframe 間メッセージ通信で使用する型定義
 */

/**
 * この通信サービスを識別するための一意な文字列。
 * プロジェクト名やバージョンを含めるとより安全。
 */
export const CORE_IFRAME_COMMUNICATOR_ID = '__CORE_IFRAME_MSG_SVC_V1__';

/**
 * メッセージの送信元
 */
export type MessageSource = 'parent' | 'child';

/**
 * 基本となるメッセージインターフェース
 */
export interface BaseMessage {
  /** このメッセージが CoreIFrameCommunicator のものであることを示す識別子 */
  serviceIdentifier: typeof CORE_IFRAME_COMMUNICATOR_ID;
  /** メッセージの一意なID */
  id: string;
  /** 送信元 */
  source: MessageSource;
  /** CORE Framework インスタンス識別子 (オプション) */
  coreInstanceId?: string;
}

/**
 * スクリプト実行リクエストのペイロード
 */
export interface ExecuteScriptPayload {
  /** 実行する関数定義の文字列 */
  functionString: string;
  /** 関数に渡す引数の配列 (オプション) */
  args?: Record<string, any>;
  /** 関数の `this` コンテキスト (オプション) */
  context?: any;
}

/**
 * リクエストメッセージ
 */
export interface RequestMessage extends BaseMessage {
  type: 'request';
  /** 実行したいアクション名 (例: 'executeScript', 'getCoreInfo') */
  action: string;
  /** アクションに必要なペイロード (型はアクションによる) */
  payload?: any | ExecuteScriptPayload;
}

/**
 * 応答メッセージ
 */
export interface ResponseMessage extends BaseMessage {
  type: 'response';
  /** 対応するリクエストのID */
  requestId: string;
  /** 処理結果のペイロード (オプション) */
  payload?: any;
  /** エラー情報 (オプション) */
  error?: {
    message: string;
    name?: string;
    stack?: string;
    details?: any; // 追加のエラー詳細
  };
}

/**
 * イベントメッセージ
 */
export interface EventMessage extends BaseMessage {
  type: 'event';
  /** イベント名 (例: 'coreReady', 'dataUpdated') */
  eventName: string;
  /** イベントに関連するペイロード (オプション) */
  payload?: any;
}

/**
 * 送受信される可能性のあるすべてのメッセージ型
 */
export type Message = RequestMessage | ResponseMessage | EventMessage;

/**
 * コミュニケータの初期化オプション
 */
export interface CommunicatorOptions {
  /**
   * 通信を許可するオリジンのリスト。
   * ChildCommunicator の場合は親ウィンドウのオリジンを指定。
   * ParentCommunicator の場合は子iframeのオリジンを指定。
   * '*' は非推奨。
   */
  allowedOrigins: string[];
  /**
   * リクエストのデフォルトタイムアウト時間 (ミリ秒)。
   * 指定しない場合はタイムアウトなし。
   */
  requestTimeout?: number;
  /**
   * デバッグログを有効にするか。
   */
  debug?: boolean;
  /**
   * CORE Framework インスタンス識別子 (オプション)。
   * 複数のCOREインスタンスが同一ページに存在する場合の区別に利用。
   */
  coreInstanceId?: string;
}

/**
 * ペンディング中のリクエスト情報を管理するためのインターフェース
 */
export interface PendingRequest {
  /** リクエストID */
  id: string;
  /** リクエスト送信時刻 */
  timestamp: number;
  /** 応答を受け取った際に解決する Promise の resolve 関数 */
  resolve: (value: any) => void;
  /** エラーまたはタイムアウト時に拒否する Promise の reject 関数 */
  reject: (reason?: any) => void;
  /** タイムアウト処理用のタイマーID (オプション) */
  timeoutTimer?: ReturnType<typeof setTimeout>;
}
